import React, { useEffect, useState } from 'react';
import { Box, Grid, useMediaQuery, useTheme } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';

// Components
import Navbar from '../Components/Common/Navbar';
import Footer from '../Components/Footer/Footer';
import Loader from '../Components/Common/Loader';
import Carousel from '../Components/Common/Carousel';
import SEO from '../../utils/SEO';

// Restaurant Components
import RestaurantHeader from './Restaurant/components/RestaurantHeader';
import RestaurantTabs from './Restaurant/components/RestaurantTabs';
import ReservationForm from './Restaurant/components/ReservationForm';
import DatePickerPopover from './Restaurant/components/DatePickerPopover';
import TimeSlotPopover from './Restaurant/components/TimeSlotPopover';
import ShareModal from './Restaurant/components/ShareModal';
import RestaurantLocation from './Restaurant/components/RestaurantLocation';

// API
import {
  addToFavourite,
  getOpeningHours,
  getSingleRestaurant,
} from '../../api';

const Restaurants = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isAuthenticated = useSelector((state) => state.user.isAuthenticated);

  // Tab state
  const [tabValue, setTabValue] = useState(0);

  // Popover states
  const [dateAnchorEl, setDateAnchorEl] = useState(null);
  const [timeAnchorEl, setTimeAnchorEl] = useState(null);

  // Modal states
  const [shareModalOpen, setShareModalOpen] = useState(false);

  // Form states
  const [guest, setGuest] = useState(1);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedFloor, setSelectedFloor] = useState(null);
  const [pointSelected, setPointSelected] = useState(null);
  const [newDeposit, setNewDeposit] = useState(null);

  // Data states
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState(null);
  const [openingHours, setOpeningHours] = useState(null);
  const [timeLoading, setTimeLoading] = useState(false);
  const [timeError, setTimeError] = useState(
    'No Available Time for the selected date',
  );
  const [fullyBooked, setFullyBooked] = useState(false);
  const [deposit, setDeposit] = useState(0);
  const [extraCharge, setExtraCharge] = useState(0);

  // Favorite states
  const [favLoading, setFavLoading] = useState(false);
  const [favSuccess, setFavSuccess] = useState(false);

  // Helper functions
  const handleAlert = (variant, message) => {
    enqueueSnackbar(message, { variant });
  };

  const resetSelectedTime = () => {
    setSelectedTime(null);
    setPointSelected(null);
    setNewDeposit(null);
  };

  // Event handlers
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleDateClick = (event) => {
    setDateAnchorEl(event.currentTarget);
  };

  const handleDateClose = () => {
    setDateAnchorEl(null);
  };

  const handleTimeClick = (event) => {
    setTimeAnchorEl(event.currentTarget);
  };

  const handleTimeClose = () => {
    setTimeAnchorEl(null);
  };

  const handleShareModalOpen = () => {
    setShareModalOpen(true);
  };

  const handleShareModalClose = () => {
    setShareModalOpen(false);
  };

  // API handlers
  const handleGetRestaurant = async () => {
    setIsLoading(true);
    try {
      const res = await getSingleRestaurant(id);
      setData(res?.data?.result[0]);
      resetSelectedTime();
      setSelectedFloor(res.data?.result[0]?.FloorPlans[0]?.Id);
    } catch (err) {
      console.error('Error fetching restaurant:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    handleGetRestaurant();
  }, [id]);

  const handleSelectTime = (time, point, timeDeposit) => {
    setSelectedTime(time);
    setPointSelected(point);
    setNewDeposit(timeDeposit);
  };

  const handleGetOpeningHours = async () => {
    setTimeLoading(true);
    setOpeningHours(null);
    try {
      const res = await getOpeningHours(
        data?.RestaurantId,
        date,
        guest,
        selectedFloor,
      );
      if (res?.data?.status) {
        setOpeningHours(res?.data?.result[0]?.GroupedTime);
        setDeposit(res?.data?.result[0]?.ReservationDeposit);
        setExtraCharge(res?.data?.result[0]?.ReservationExtraCharge);

        setFullyBooked(res?.data?.success_message === 'Fully booked');
      } else {
        setOpeningHours(null);
        setFullyBooked(res?.data?.error_message === 'Fully booked');
        if (res?.data?.error_message !== 'Fully booked') {
          handleAlert('error', res?.data?.error_message);
          setTimeError(res?.data?.error_message);
        }
      }
    } catch (err) {
      handleAlert('error', err?.message);
      setOpeningHours(null);
      resetSelectedTime();
    } finally {
      setTimeLoading(false);
    }
  };

  useEffect(() => {
    if (data) {
      handleGetOpeningHours();
    }
  }, [id, date, guest, data, selectedFloor]);

  const handleAddToFavorite = async () => {
    setFavLoading(true);
    setFavSuccess(false);
    try {
      await addToFavourite(id);
      setFavSuccess(true);
    } catch (err) {
      console.error('Error adding to favorite:', err);
    } finally {
      setFavLoading(false);
    }
  };

  const handleLoginRedirect = () => {
    navigate('/login', {
      state: { redirectTo: location.pathname },
    });
  };

  const handleBookReservation = () => {
    const reservationDeposit = newDeposit?.Amount ?? deposit;
    const reservationCharges = newDeposit?.ExtraCharge ?? extraCharge;
    const params = new URLSearchParams({
      restaurantName: data?.QuerySlot || '',
      guest,
      time: selectedTime || '',
      date,
      deposit: reservationDeposit,
      floor: selectedFloor || '',
      pointValue: pointSelected?.Value || null,
      point_id: pointSelected?.Id || null,
      charges: reservationCharges,
      waitlist: fullyBooked,
    });

    navigate(`/reservation/user?${params.toString()}`);
  };

  const handleDateSelect = (selectedDate) => {
    setDate(selectedDate);
    resetSelectedTime();
  };

  const handleTimeSelect = (time, point, timeDeposit) => {
    handleSelectTime(time, point, timeDeposit);
  };

  const handleGuestChange = (newGuest) => {
    setGuest(newGuest);
    resetSelectedTime();
  };

  const handleFloorSelect = (floorId) => {
    setSelectedFloor(floorId);
    resetSelectedTime();
  };

  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <>
      <SEO
        title={`${id} | Explore and Book Reservation with Reisty in Lagos - Instantly`}
        description={`Explore the best dining experiences ${id} offers with Reisty! From trendy cafes to exquisite fine dining, find menus, read reviews, and book your table online today.`}
        name="Reisty"
        type="article"
        canonicalUrl={`https:/reisty.com/restaurant/${id}`}
      />
      {isLoading && <Loader />}
      <Navbar />

      <Box
        sx={{
          pt: { lg: 10, md: 10, sm: 10, xs: 9 },
          width: { xl: '75%', lg: '85%', sm: '90%', xs: '100%' },
          margin: '0 auto',
        }}
      >
        {/* Carousel Section */}
        <Box
          sx={{
            bgcolor: '#ccc',
            height: { lg: '400px', md: '400px', sm: '300px', xs: '250px' },
          }}
        >
          {!isLoading && (
            <Carousel
              images={data?.Photos}
              title={data?.RestaurantName}
              description={data?.Description}
            />
          )}
        </Box>

        {/* Main Content */}
        <Box
          sx={{
            width: { lg: '100%', md: '100%', sm: '100%', xs: '90%' },
            margin: '0 auto',
          }}
        >
          <Grid container spacing={4} sx={{ mt: 3, mb: 16 }}>
            {/* Left Column - Restaurant Info & Tabs */}
            <Grid item lg={7.5} md={7.5} xs={12}>
              <Box>
                {/* Restaurant Header */}
                <RestaurantHeader
                  data={data}
                  favLoading={favLoading}
                  favSuccess={favSuccess}
                  onAddToFavorite={handleAddToFavorite}
                  onShare={handleShareModalOpen}
                  isAuthenticated={isAuthenticated}
                  onLoginRedirect={handleLoginRedirect}
                />
                {/* Restaurant Tabs */}

                <RestaurantTabs
                  value={tabValue}
                  onTabChange={handleTabChange}
                  data={data}
                  handleSelectTime={handleSelectTime}
                  fullyBooked={fullyBooked}
                  guest={guest}
                  timeError={timeError}
                  selectedFloor={selectedFloor}
                  setSelectedFloor={handleFloorSelect}
                  isLoading={timeLoading}
                  handleClose2={handleTimeClose}
                  openingHours={openingHours}
                  selectedTime={selectedTime}
                  setSelectedTime={setSelectedTime}
                  date={date}
                  onTimeClick={handleTimeClick}
                  onDateClick={handleDateClick}
                  onGuestChange={handleGuestChange}
                  onBook={handleBookReservation}
                  isAuthenticated={isAuthenticated}
                  onLoginRedirect={handleLoginRedirect}
                />

                <Box sx={{ mt: 3, display: { md: 'block', xs: 'none' } }}>
                  {/* Location Information */}
                  <RestaurantLocation data={data} />
                </Box>
              </Box>
            </Grid>

            {/* Right Column - Reservation Form & Location */}
            <Grid item lg={4.5} md={4.5} sm={12} xs={12}>
              <Box sx={{ position: 'sticky', top: 100 }}>
                {/* Reservation Form */}
                <ReservationForm
                  onlyButton={isMobile ? true : false}
                  data={data}
                  selectedTime={selectedTime}
                  date={date}
                  guest={guest}
                  onTimeClick={handleTimeClick}
                  onDateClick={handleDateClick}
                  onGuestChange={handleGuestChange}
                  onBook={handleBookReservation}
                  isAuthenticated={isAuthenticated}
                  onLoginRedirect={handleLoginRedirect}
                />

                <Box sx={{ mt: 3, display: { md: 'none', xs: 'block' } }}>
                  {/* Location Information */}
                  <RestaurantLocation data={data} />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
      <Footer />

      {/* Date Picker Popover */}
      <DatePickerPopover
        open={Boolean(dateAnchorEl)}
        anchorEl={dateAnchorEl}
        onClose={handleDateClose}
        onDateSelect={handleDateSelect}
      />

      {/* Time Slot Popover */}
      <TimeSlotPopover
        open={Boolean(timeAnchorEl)}
        anchorEl={timeAnchorEl}
        onClose={handleTimeClose}
        openingHours={openingHours}
        selectedTime={selectedTime}
        onTimeSelect={handleTimeSelect}
      />

      {/* Share Modal */}
      <ShareModal
        open={shareModalOpen}
        onClose={handleShareModalClose}
        restaurantName={data?.RestaurantName}
        restaurantId={id}
      />
    </>
  );
};

export default Restaurants;
